// frontend/src/components/payments/BillingPage.js
import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Clock, 
  Calendar, 
  DollarSign, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Plus, 
  Edit, 
  Trash2, 
  Download, 
  Eye,
  Loader,
  Shield,
  Zap,
  Building,
  Crown,
  User,
  Bell,
  Info
} from 'lucide-react';
import { useSubscription } from '../../hooks/useSubscription';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import PaymentMethodSetup from './PaymentMethodSetup';
import PaymentMethodList from './PaymentMethodList';
import PaymentHistory from './PaymentHistory';
import SubscriptionCancellation from './SubscriptionCancellation';
import { PageHeader, Badge } from '../common';
import toast from 'react-hot-toast';
import api from '../../services/api';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const BillingPage = () => {
  const { subscription, refreshSubscription } = useSubscription();
  const [activeTab, setActiveTab] = useState('overview');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentHistoryError, setPaymentHistoryError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAddPaymentMethod, setShowAddPaymentMethod] = useState(false);
  const [autoRenew, setAutoRenew] = useState(true);

  // Load payment methods and history on component mount
  useEffect(() => {
    loadPaymentMethods();
    loadPaymentHistory();
    // Initialize toggles from subscription state if available
    if (subscription) {
      // If the subscription is set to cancel at period end, auto-renew is off
      const inferredAutoRenew = !subscription.cancel_at_period_end && !subscription.cancelled;
      setAutoRenew(inferredAutoRenew);
    }
    // Load notification preference for 7-day renewal reminder if available in backend later
    // For now default false; could be fetched from api.getNotificationSettings with a key
  }, []);

  const loadPaymentMethods = async () => {
    try {
      const response = await api.getPaymentMethods();
      setPaymentMethods(response.data || []);
    } catch (error) {
      console.error('Failed to load payment methods:', error);
    }
  };

  const loadPaymentHistory = async () => {
    try {
      const response = await api.getBillingHistory();
      setPaymentHistory(response.data || []);
      setPaymentHistoryError(false);
    } catch (error) {
      // Not an empty list: "no payments" would be a false claim to a payer.
      console.error('Failed to load payment history:', error);
      setPaymentHistoryError(true);
    }
  };

  const handlePaymentMethodAdded = async (paymentMethodId) => {
    setShowAddPaymentMethod(false);
    await loadPaymentMethods();
    toast.success('Payment method added successfully!');
  };

  const handlePaymentMethodDeleted = async (paymentMethodId) => {
    try {
      await api.deletePaymentMethod(paymentMethodId);
      await loadPaymentMethods();
      toast.success('Payment method removed successfully!');
    } catch (error) {
      toast.error('Failed to remove payment method');
    }
  };

  const handleSubscriptionCancelled = async () => {
    await refreshSubscription();
    toast.success('Subscription cancelled successfully');
  };

  // Subscription plan configuration
  const getPlanInfo = (tier) => {
    const plans = {
      free: { name: 'Free', icon: User, color: 'gray', price: 0 },
      basic: { name: 'Basic', icon: User, color: 'blue', price: 8.99 },
      // Canonical mechanic tiers (migration 045). Without these keys a paying
      // mechanic fell through to plans.free and their billing page read "Free".
      mechanic_essential: { name: 'Mechanic Pro', icon: Zap, color: 'green', price: 29.99 },
      mechanic_pro: { name: 'Mechanic Premium', icon: Building, color: 'purple', price: 49.99 },
      essential: { name: 'Mechanic Pro', icon: Zap, color: 'green', price: 29.99 },
      // Car-owner Pro. Was 8.99, drifted from the backend's SUBSCRIPTION_TIERS
      // (1.99), so a paying Pro owner saw the wrong price here.
      professional: { name: 'Pro', icon: Building, color: 'purple', price: 1.99 },
      shop_pro: { name: 'Shop Pro', icon: Building, color: 'indigo', price: 99 },
      enterprise: { name: 'Enterprise', icon: Crown, color: 'gold', price: 299 }
    };
    return plans[tier] || plans.free;
  };

  const planInfo = getPlanInfo(subscription?.tier);

  // Calculate days until renewal
  const getDaysUntilRenewal = () => {
    if (!subscription?.current_period_end) return null;
    const renewalDate = new Date(subscription.current_period_end);
    const today = new Date();
    const diffTime = renewalDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntilRenewal = getDaysUntilRenewal();
  const showRenewalNotice = daysUntilRenewal !== null && daysUntilRenewal >= 0 && daysUntilRenewal <= 7 && autoRenew;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Eye },
    { id: 'payment-methods', label: 'Payment Methods', icon: CreditCard },
    { id: 'payment-history', label: 'Payment History', icon: Clock },
    { id: 'subscription', label: 'Subscription', icon: Calendar }
  ];

  return (
    <div className="max-w-7xl mx-auto">
      <PageHeader
        title="Billing"
        subtitle="Manage your subscription, payment methods, and billing history"
      />

      <div>
        {/* Upcoming Renewal Notice */}
        {showRenewalNotice && (
          <div className="mb-6 bg-accent-tint border border-accent-border p-4 rounded-card">
            <div className="flex items-start">
              <Bell className="w-5 h-5 text-accent mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-sm font-semibold text-accent">
                    Upcoming Renewal
                  </h3>
                </div>
                <p className="text-sm text-ink-soft">
                  {daysUntilRenewal === 0 ? (
                    <>Your subscription renews <strong>today</strong>. You'll be charged <strong className="font-mono tabular-nums">${planInfo.price}</strong> for your {planInfo.name} plan.</>
                  ) : daysUntilRenewal === 1 ? (
                    <>Your subscription renews <strong>tomorrow</strong>. You'll be charged <strong className="font-mono tabular-nums">${planInfo.price}</strong> for your {planInfo.name} plan.</>
                  ) : (
                    <>Your subscription renews in <strong>{daysUntilRenewal} days</strong> on <strong>{new Date(subscription.current_period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>. You'll be charged <strong>${planInfo.price}</strong> for your {planInfo.name} plan.</>
                  )}
                </p>
                <div className="mt-3 flex items-center gap-3">
                  <button
                    onClick={() => setActiveTab('payment-methods')}
                    className="text-sm font-medium text-accent hover:text-accent-hover underline"
                  >
                    Update payment method
                  </button>
                  <span className="text-ink-faint">•</span>
                  <button
                    onClick={() => setActiveTab('subscription')}
                    className="text-sm font-medium text-accent hover:text-accent-hover underline"
                  >
                    Manage subscription
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-surface-card rounded-card border border-border-default mb-8">
          <div className="border-b border-border-default">
            {/* Scrolls on its own at 390px; it used to push the page 156px wide. */}
            <nav className="flex gap-6 sm:gap-8 px-4 sm:px-6 overflow-x-auto scrollbar-hide">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-1 border-b-2 text-body flex items-center whitespace-nowrap flex-shrink-0 min-h-touch transition-colors duration-state ${
                      activeTab === tab.id
                        ? 'border-accent text-accent font-semibold'
                        : 'border-transparent text-ink-muted font-medium hover:text-ink'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" strokeWidth={1.5} aria-hidden="true" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-4 sm:p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Current Subscription Status. Was a gradient panel with three
                    white cards nested inside it; now one bordered surface with
                    its figures in a divided row. */}
                {subscription && (
                  <div className="rounded-card border border-border-default">
                    <div className="flex items-center justify-between gap-4 p-4 sm:p-5">
                      <div className="flex items-center min-w-0">
                        {(() => {
                          const Icon = planInfo.icon;
                          // Tier colour is metadata, not a primary action -- one accent
                          // per screen; each plan already has a distinct Icon shape.
                          return <Icon className="w-6 h-6 text-ink-soft mr-3 flex-shrink-0" strokeWidth={1.5} aria-hidden="true" />;
                        })()}
                        <div className="min-w-0">
                          <h2 className="text-section-title font-display text-ink">{planInfo.name} Plan</h2>
                          <p className="text-small text-ink-muted">
                            <span className="font-mono tabular-nums">${planInfo.price}</span>/month
                          </p>
                        </div>
                      </div>
                      <Badge status={subscription.status === 'active' ? 'success' : 'danger'}>
                        {subscription.status === 'active' ? (
                          <CheckCircle className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden="true" />
                        ) : (
                          <XCircle className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden="true" />
                        )}
                        {subscription.status}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 border-t border-border-default divide-y md:divide-y-0 md:divide-x divide-border-default">
                      <div className="p-4 sm:p-5">
                        <div className="text-label uppercase text-ink-muted mb-2">Next Billing</div>
                        <div className="text-2xl font-mono tabular-nums font-semibold text-ink">
                          {subscription.current_period_end
                            ? new Date(subscription.current_period_end).toLocaleDateString()
                            : '—'}
                        </div>
                        <div className="text-small text-ink-muted mt-1">
                          {/* Was "N/A / month billing". */}
                          {subscription.current_period_end
                            ? `${['year', 'yearly', 'annual'].includes(subscription.billing_interval) ? 'Yearly' : 'Monthly'} billing`
                            : 'No renewal scheduled'}
                        </div>
                        <label className="mt-4 inline-flex items-center gap-2 text-body text-ink-soft min-h-touch cursor-pointer">
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-accent cursor-pointer"
                            checked={autoRenew}
                            onChange={async (e) => {
                              const next = e.target.checked;
                              setAutoRenew(next);
                              try {
                                if (next) {
                                  await api.resumeSubscription();
                                  toast.success('Auto-renew enabled');
                                } else {
                                  await api.cancelSubscription();
                                  toast.success('Auto-renew disabled. Will cancel at period end');
                                }
                                await refreshSubscription();
                              } catch (err) {
                                setAutoRenew(!next);
                                toast.error('Failed to update auto-renew');
                              }
                            }}
                          />
                          Auto-renew
                        </label>
                      </div>

                      <div className="p-4 sm:p-5">
                        <div className="text-label uppercase text-ink-muted mb-2">Payment Methods</div>
                        <div className="text-2xl font-mono tabular-nums font-semibold text-ink">
                          {paymentMethods.length}
                        </div>
                        <div className="text-small text-ink-muted mt-1">
                          {paymentMethods.length === 0 ? 'No payment methods' :
                           paymentMethods.length === 1 ? '1 payment method' :
                           `${paymentMethods.length} payment methods`}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* The three "quick action" cards that were here repeated the
                    tab row directly above them, one for one. */}

                {/* FAQ Section */}
                <div className="pt-6 border-t border-border-default">
                  <h2 className="text-section-title font-display text-ink mb-4">Frequently Asked Questions</h2>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-body-strong text-ink mb-1">Can I change my plan anytime?</h3>
                      <p className="text-body text-ink-muted">Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.</p>
                    </div>
                    <div>
                      <h3 className="text-body-strong text-ink mb-1">Can I cancel my subscription?</h3>
                      <p className="text-body text-ink-muted">Yes, you can cancel anytime. You'll retain access until the end of your billing period.</p>
                    </div>
                    <div>
                      <h3 className="text-body-strong text-ink mb-1">How do I update my payment method?</h3>
                      <p className="text-body text-ink-muted">Go to the Payment Methods tab to add, edit, or remove payment methods.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'payment-methods' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-ink-soft">Payment Methods</h2>
                  <button
                    onClick={() => setShowAddPaymentMethod(true)}
                    className="bg-accent text-white px-4 py-2 rounded-lg hover:bg-accent-hover flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Payment Method
                  </button>
                </div>

                {showAddPaymentMethod ? (
                  <div className="bg-surface-page rounded-lg p-6 border-2 border-dashed border-border-default">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-ink-soft">Add New Payment Method</h3>
                      <button
                        onClick={() => setShowAddPaymentMethod(false)}
                        className="text-ink-muted hover:text-ink-muted"
                      >
                        <XCircle className="w-6 h-6" />
                      </button>
                    </div>
                    <Elements stripe={stripePromise}>
                      <PaymentMethodSetup
                        onSuccess={handlePaymentMethodAdded}
                        onCancel={() => setShowAddPaymentMethod(false)}
                      />
                    </Elements>
                  </div>
                ) : (
                  <PaymentMethodList
                    paymentMethods={paymentMethods}
                    onDelete={handlePaymentMethodDeleted}
                    onEdit={() => setShowAddPaymentMethod(true)}
                  />
                )}
              </div>
            )}

            {activeTab === 'payment-history' && (
              <PaymentHistory
                paymentHistory={paymentHistory}
                error={paymentHistoryError}
                onRefresh={loadPaymentHistory}
              />
            )}

            {activeTab === 'subscription' && (
              <SubscriptionCancellation
                subscription={subscription}
                onCancelled={handleSubscriptionCancelled}
                planInfo={planInfo}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingPage;
